import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import { chapters } from '@/mocks/chapters';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const chapter = useMemo(() => chapters.find((ch) => ch.slug === slug), [slug]);

  if (!chapter) {
    return <Navigate to="/" replace />;
  }

  const currentIdx = chapters.findIndex((ch) => ch.slug === slug);
  const prevChapter = currentIdx > 0 ? chapters[currentIdx - 1] : null;
  const nextChapter = currentIdx < chapters.length - 1 ? chapters[currentIdx + 1] : null;

  const category = categories.find((c) => c.id === chapter.categoryId);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: chapter.title,
    description: chapter.excerpt,
    author: {
      '@type': 'Person',
      name: siteInfo.author,
    },
    datePublished: chapter.publishDate,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'Book',
      name: siteInfo.name,
      url: 'https://naptevq.cn/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naptevq.cn/' },
      ...(category
        ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `https://naptevq.cn/category/${category.slug}` }]
        : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: chapter.title, item: `https://naptevq.cn/article/${chapter.slug}` },
    ],
  };

  const contentParagraphs = chapter.content.split('\n\n').filter((p) => p.trim());

  return (
    <>
      <SEO
        title={`${chapter.title} - 艳母的荒唐赌约 | 在线阅读`}
        description={chapter.excerpt}
        keywords={`艳母的荒唐赌约,${chapter.title},${chapter.categoryName},小说章节,在线阅读`}
        canonicalUrl={`https://naptevq.cn/article/${chapter.slug}`}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              {category && (
                <>
                  <Link to={`/category/${category.slug}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                    {category.name}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
                </>
              )}
              <span className="text-foreground-900 font-medium line-clamp-1">{chapter.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Chapter Header */}
            <header className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-xs text-foreground-500 bg-secondary-100 px-3 py-1 rounded-full">
                  {chapter.categoryName}
                </span>
                <span className="text-xs text-foreground-500">
                  第 {chapter.chapterNumber} 章
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {chapter.title}
              </h1>
              <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
                <span>{chapter.publishDate}</span>
                <span>约 {chapter.wordCount} 字</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(chapter.viewCount / 10000).toFixed(1)}万 阅读
                </span>
              </div>
            </header>

            {/* Chapter Content */}
            <div className="prose-custom text-foreground-800 leading-loose">
              {contentParagraphs.map((paragraph, idx) => (
                <p key={idx} className="mb-6 text-base md:text-lg text-foreground-800 leading-relaxed indent-8">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            {/* Chapter Navigation */}
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex items-center justify-between gap-4">
                {prevChapter ? (
                  <Link
                    to={`/article/${prevChapter.slug}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    <span className="hidden sm:inline">上一章：</span>
                    <span className="line-clamp-1">{prevChapter.title}</span>
                  </Link>
                ) : (
                  <div></div>
                )}
                {nextChapter ? (
                  <Link
                    to={`/article/${nextChapter.slug}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="hidden sm:inline">下一章：</span>
                    <span className="line-clamp-1">{nextChapter.title}</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                ) : (
                  <div></div>
                )}
              </div>
            </div>

            {/* Back to Category */}
            {category && (
              <div className="mt-6 text-center">
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-list-check"></i>
                  返回{category.name}目录
                </Link>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}